/**
 * Head-to-head summary of one run.
 *
 * Wall-clock times ARE comparable here, because the models run concurrently on
 * SEPARATE GPUs — neither waits for the other and neither contends for the same
 * card. Two things would break that, and both are handled rather than hidden:
 *
 *   - A model that cold-started inside the run has its ~4-minute GPU boot
 *     buried in its time. Comparing that against a warm model measures the
 *     boot, not the model, so no winner is declared.
 *   - A model that errored has no meaningful time at all.
 */

import { Card } from "@/components/ui";
import type { ModelInfo, RunResult } from "@/lib/types";

function fmt(ms: number) {
  return ms >= 10_000 ? `${(ms / 1000).toFixed(0)}s` : `${(ms / 1000).toFixed(1)}s`;
}

export function ComparisonBar({ models, results }: { models: ModelInfo[]; results: RunResult[] }) {
  const rows = models
    .map((m) => ({ model: m, r: results.find((x) => x.modelId === m.id) }))
    .filter((x): x is { model: ModelInfo; r: RunResult } => Boolean(x.r));

  if (rows.length < 1) return null;

  const ok = rows.filter((x) => x.r.ok);
  const slowest = Math.max(...ok.map((x) => x.r.ms), 1);
  const comparable = ok.length === 2 && ok.every((x) => x.r.wasWarm !== false);
  const [fast, slow] = comparable ? [...ok].sort((a, b) => a.r.ms - b.r.ms) : [];
  const ratio = comparable ? slow.r.ms / Math.max(fast.r.ms, 1) : 0;

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-fh-body font-medium text-fh-text-strong">This run</h2>
        {comparable && (
          <p className="text-fh-meta text-fh-text-secondary">
            <span className="font-medium text-fh-text-strong">{fast.model.label}</span> was{" "}
            {ratio >= 1.05 ? `${ratio.toFixed(1)}× faster` : "about the same speed"}
          </p>
        )}
      </div>

      <div className="space-y-2.5">
        {rows.map(({ model, r }) => {
          const tps = r.usage?.completion_tokens && r.ms
            ? Math.round(r.usage.completion_tokens / (r.ms / 1000))
            : null;
          return (
            <div key={model.id}>
              <div className="flex items-baseline justify-between gap-3 text-fh-meta">
                <span className="font-medium text-fh-text-body">{model.label}</span>
                <span className="text-fh-text-secondary">
                  {r.ok ? (
                    <>
                      <span className="font-medium text-fh-text-strong">{fmt(r.ms)}</span>
                      {r.usage?.completion_tokens != null && <> · {r.usage.completion_tokens} tok</>}
                      {tps != null && <> · {tps} tok/s</>}
                      {r.blocks.length > 0 && <> · {r.blocks.length} blocks</>}
                    </>
                  ) : (
                    <span className="text-fh-danger">failed</span>
                  )}
                </span>
              </div>
              {/* Bar is relative to the slower of the two, so the gap is the
                  thing you see rather than an absolute scale nobody can read. */}
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-fh-surface-avatar">
                <div
                  className={r.ok ? "h-full rounded-full bg-fh-navy" : "h-full rounded-full bg-fh-danger/40"}
                  style={{ width: r.ok ? `${Math.max((r.ms / slowest) * 100, 3)}%` : "100%" }}
                />
              </div>
              {r.wasWarm === false && (
                <p className="mt-1 text-fh-meta text-fh-priority-normal">
                  Cold start included — this model was not running when the comparison began, so
                  its time is mostly GPU startup.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {!comparable && ok.length === 2 && (
        <p className="mt-3 border-t border-fh-border-soft pt-2 text-fh-meta text-fh-text-muted">
          No winner shown: one model cold-started during this run, so the times measure different
          things. Start both, wait for Ready, then compare again for a like-for-like number.
        </p>
      )}
    </Card>
  );
}
